/* MODELLO DELLA SCHERMATA TRADEBOT — dal record del backend a ciò che si legge.
 *
 * Tutto quello che qui viene "derivato" è derivato perché il backend non lo
 * espone: la entity Tradebot non ha né drawdown, né numero di operazioni, né
 * una nozione di posizione aperta. Sta tutto nei `trades`, che arrivano
 * comunque perché la relazione è EAGER.
 *
 * Il calcolo sta in un file solo — e non dentro i componenti — per due motivi:
 * la riga di lista e il dettaglio devono dire la STESSA cosa dello stesso bot
 * (uno stato calcolato in due posti diventa due stati diversi), e il giorno in
 * cui il backend esporrà questi campi (RICHIESTE-API.md §2) si cancella questo
 * file, non sette componenti.
 */

import type { ParametriCrud, Trade, Tradebot } from '../../api';
import { etichettaCapitale } from '../../componenti/BotInFunzione';
import { durataPeriodo, num } from '../../lib/formato';
import type { Tono } from '../../lib/metriche';
import { vitalitaDi } from '../../lib/vitalita';

/* ==========================================================================
 * Cosa chiedere al backend
 * ======================================================================== */

/**
 * Colonne della riga di lista.
 *
 * `fields` NON tocca le relazioni eager: `trades`, `account` e `wallet`
 * arrivano comunque. È esattamente il motivo della richiesta §2 in
 * RICHIESTE-API.md. Finché resta così, i trade che paghiamo in banda li usiamo
 * almeno per calcolare la terna, invece di mostrare tre "n/d".
 *
 * `telegramToken` e `telegramPassword` sono deliberatamente FUORI: sono
 * credenziali, e una lista che le porta le mette in rete a ogni caricamento di
 * pagina. La clonazione, che ne ha bisogno, rilegge il bot intero.
 */
export const CAMPI_RIGA_BOT: ParametriCrud = {
  fields: [
    'id',
    'isRunning',
    'PID',
    'useRealMoney',
    'usePartialBalance',
    'accountId',
    'walletId',
    'marketWatcherId',
    'dex',
    'chainId',
    'rpc',
    'gasLimit',
    'swapSlippage',
    'minimumBaseCoinBalance',
    'rechargeQtyBaseCoinBalance',
    'asset',
    'currency',
    'strategyName',
    'strategyParams',
    'candleSize',
    'warmupPeriod',
    'startDate',
    'endDate',
    'initialValue',
    'initialAssetPrice',
    'assetBalance',
    'currencyBalance',
    'parkedCurrency',
    'marketChange',
    'strategyChange',
    'finalAssetPrice',
    'finalTotalBalance',
    'failedTransactions',
    'errors',
    'createdAt',
    'updatedAt',
    /* Il battito, il prezzo che il bot vede e da quando misura. Servono perché
       `periodoBot` etichetta il periodo con `measuredSince` quando c'è, e
       `lib/vitalita.ts` legge il battito su `lastSeenAt`: senza queste tre
       colonne la lista direbbe di uno stesso bot cose diverse dal dettaglio —
       un altro periodo di misura, e «battito non misurato» su un bot vivo. */
    'lastSeenAt',
    'lastSeenPrice',
    'measuredSince',
  ],
  sort: ['id,DESC'],
};

/* ==========================================================================
 * Modalità — denaro vero o simulato, su exchange o su catena
 * ======================================================================== */

export interface ModalitaBot {
  /** `useRealMoney`. Va SEMPRE a schermo: è la differenza fra una prova e i soldi. */
  denaroReale: boolean;
  /** Il bot firma swap on-chain: ha un wallet e una catena, non un account CEX. */
  suDex: boolean;
  /** Etichetta del capitale: REALE o SIMULATO. Mai omessa. */
  etichettaCapitale: string;
  /** Dove opera: "CEX" o "DEX · catena 56". */
  etichettaSede: string;
}

export function modalitaBot(bot: Tradebot): ModalitaBot {
  const suDex = bot.walletId != null;
  return {
    denaroReale: bot.useRealMoney,
    suDex,
    /* Le due parole arrivano dal vocabolario condiviso: la lista dei backtest e
       il dettaglio del wallet dicono «REALE» esattamente così, e una terza
       variante scritta qui sarebbe la prima a divergere. */
    etichettaCapitale: etichettaCapitale(bot.useRealMoney),
    etichettaSede: suDex ? `DEX${bot.chainId != null ? ` · catena ${bot.chainId}` : ''}` : 'CEX',
  };
}

/* ==========================================================================
 * Stato operativo
 * ======================================================================== */

export type ChiaveStatoBot =
  | 'fermo'
  | 'in-arresto'
  | 'in-posizione'
  | 'attivo-fuori'
  | 'non-respira'
  | 'errore';

export interface StatoBot {
  chiave: ChiaveStatoBot;
  etichetta: string;
  tono: Tono;
  /** Il pallino lampeggia solo quando qualcosa sta ancora per succedere. */
  pulsa: boolean;
  /** Frase che spiega lo stato quando l'etichetta da sola mentirebbe. */
  nota?: string;
}

/**
 * Lo stato di un bot NON è `isRunning`.
 *
 * `PATCH isRunning:false` risponde subito, ma è il processo esterno a reagirci
 * quando se ne accorge: fra la richiesta e l'arresto vero il bot può essere
 * ancora dentro una posizione. Scrivere "Fermo" in quell'intervallo, su un bot
 * che opera con denaro vero, è la bugia più costosa che questa schermata possa
 * dire. Finché non esiste `POST /tradebots/:id/stop` (RICHIESTE-API.md §6) lo
 * stato intermedio va mostrato per quello che è.
 */
export function statoBot(bot: Tradebot, arrestoRichiestoA?: Date | null): StatoBot {
  if (arrestoRichiestoA && bot.isRunning) {
    return {
      chiave: 'in-arresto',
      etichetta: 'In arresto',
      tono: 'cautela',
      pulsa: true,
      nota: 'Arresto richiesto: il processo lo raccoglie alla prossima candela. La posizione aperta non viene liquidata.',
    };
  }

  if (!bot.isRunning) {
    return {
      chiave: 'fermo',
      etichetta: 'Fermo',
      tono: 'neutro',
      pulsa: false,
      nota:
        posizione(bot).aperta
          ? 'Fermo con una posizione ancora aperta: il capitale è a mercato e nessuno lo sorveglia.'
          : undefined,
    };
  }

  if ((bot.errors?.length ?? 0) > 0) {
    return {
      chiave: 'errore',
      etichetta: 'Errore',
      tono: 'perdita',
      pulsa: false,
      nota: bot.errors?.[bot.errors.length - 1] ?? undefined,
    };
  }

  /* Lo stato «warm-up» NON esiste, ed e' stato tolto.
   *
   * Era calcolato come «ore dall'avvio minori delle ore di warm-up», e con 3.000 candele da
   * un'ora dava centoventicinque giorni: un bot avviato stamattina, che alle 14:20 aveva gia'
   * ripercorso tutto lo storico e preso posizione, sarebbe apparso «in riscaldamento» fino a
   * dicembre.
   *
   * L'errore era concettuale, non aritmetico: il warm-up e' storia PASSATA, che il bot scarica
   * e consuma in pochi secondi all'avvio prima di prendere la prima decisione. Non e' un'attesa
   * che si svolge nel tempo reale, quindi non e' uno stato in cui il bot possa trovarsi mentre
   * lo si guarda.
   *
   * Un bot avviato che non ha ancora operazioni e' semplicemente attivo e fuori mercato: la
   * strategia ha gia' deciso, e ha deciso di stare fuori.
   */

  /* Risulta in marcia, ma il processo non da' segni di vita.
   *
   * Va PRIMA della posizione, ed e' il punto di tutta la modifica: un bot il cui processo e'
   * morto tenendo una posizione aperta continuava a mostrare «In posizione» in verde, cioe' lo
   * stato piu' rassicurante della lista, mentre il capitale era a mercato e nessuno lo
   * sorvegliava. `isRunning` e' solo una colonna che dice cosa era stato deciso, non cosa sta
   * succedendo; il battito dice cosa sta succedendo.
   *
   * Non si distingue fra «il bot e' morto» e «si e' fermato il flusso delle candele»: da qui
   * non si puo' sapere, e sceglierne una delle due sarebbe inventare. Entrambe vanno guardate. */
  const vita = vitalitaDi(bot);
  if (vita.allarme) {
    const aperta = posizione(bot).aperta;
    return {
      chiave: 'non-respira',
      etichetta: 'Non respira',
      tono: vita.stato === 'muto' ? 'perdita' : 'cautela',
      pulsa: true,
      nota: aperta
        ? `${vita.testo}. C'è una posizione aperta: il capitale è a mercato.`
        : `${vita.testo}.`,
    };
  }

  if (posizione(bot).aperta) {
    return { chiave: 'in-posizione', etichetta: 'In posizione', tono: 'guadagno', pulsa: false };
  }

  return {
    chiave: 'attivo-fuori',
    etichetta: 'Attivo · fuori mercato',
    tono: 'neutro',
    pulsa: false,
  };
}



/* ==========================================================================
 * Posizione aperta
 * ======================================================================== */

export interface PosizioneBot {
  aperta: boolean;
  /** L'operazione comprata e non ancora venduta. */
  trade: Trade | null;
  prezzoIngresso: number | null;
  /** L'ultimo prezzo che il bot ha visto: quello del battito se c'è, altrimenti quello
   *  dell'ultima operazione chiusa — che può essere vecchio di mesi. */
  prezzoScritto: number | null;
  quantita: number | null;
  /** Guadagno non realizzato in percentuale, `null` se non calcolabile. */
  variazionePct: number | null;
  /** Guadagno non realizzato in valuta di quotazione. */
  variazioneValuta: number | null;
  /** Valore corrente della posizione, ai prezzi scritti dal bot. */
  valore: number | null;
}

const POSIZIONE_VUOTA: PosizioneBot = {
  aperta: false,
  trade: null,
  prezzoIngresso: null,
  prezzoScritto: null,
  quantita: null,
  variazionePct: null,
  variazioneValuta: null,
  valore: null,
};

/**
 * Spot long/flat: o si è dentro con l'asset in mano, o si è fuori in valuta.
 * Il segnale primario è l'operazione comprata e non ancora venduta; il saldo
 * dell'asset serve solo a confermarlo, perché una `sellDate` scritta a metà
 * lascerebbe la riga in uno stato ambiguo.
 */
export function posizione(bot: Tradebot): PosizioneBot {
  const aperti = (bot.trades ?? []).filter((t) => t.buyDate != null && t.sellDate == null);
  const trade = aperti.length > 0 ? aperti[aperti.length - 1] : null;
  const conAsset = (bot.assetBalance ?? 0) > 0;

  if (!trade && !conAsset) return POSIZIONE_VUOTA;

  const prezzoIngresso = trade?.buyPrice ?? bot.initialAssetPrice ?? null;
  /* Il prezzo del battito viene prima di quello dell'ultima operazione chiusa.
   *
   * `finalAssetPrice` e' scritto solo quando il bot CHIUDE un'operazione: su un bot che tiene
   * una posizione da mesi e' vecchio di mesi, e su un bot che non ha mai chiuso niente e' nullo
   * -- e allora «ultimo prezzo scritto» e «non realizzato» mostravano una lineetta mentre poco
   * sopra, sulla stessa schermata, il capitale era gia' valutato al prezzo corrente. Due numeri
   * che vengono dallo stesso fatto non possono contraddirsi nella stessa pagina. */
  const prezzoScritto = bot.lastSeenPrice ?? bot.finalAssetPrice ?? null;
  const quantita = bot.assetBalance ?? null;

  const variazionePct =
    prezzoIngresso != null && prezzoScritto != null && prezzoIngresso !== 0
      ? (prezzoScritto / prezzoIngresso - 1) * 100
      : null;

  const valore = quantita != null && prezzoScritto != null ? quantita * prezzoScritto : null;

  const variazioneValuta =
    quantita != null && prezzoScritto != null && prezzoIngresso != null
      ? quantita * (prezzoScritto - prezzoIngresso)
      : null;

  return {
    aperta: true,
    trade,
    prezzoIngresso,
    prezzoScritto,
    quantita,
    variazionePct,
    variazioneValuta,
    valore,
  };
}

/* ==========================================================================
 * La terna — profitto, drawdown, operazioni
 * ======================================================================== */

export interface PuntoEquityBot {
  timestamp: number;
  value: number;
}

/**
 * La curva del capitale ricostruita dalle operazioni chiuse.
 * Il backend non salva una equity line per i tradebot (ce l'ha solo per i
 * backtest), quindi l'unico punto misurato che abbiamo è il saldo alla
 * chiusura di ogni operazione.
 */
export function equityDaTrade(bot: Tradebot): PuntoEquityBot[] {
  const chiusi = (bot.trades ?? [])
    .filter((t) => t.sellDate != null && Number.isFinite(t.exitBalance ?? NaN))
    .sort((a, b) => new Date(a.sellDate!).getTime() - new Date(b.sellDate!).getTime());

  const punti: PuntoEquityBot[] = [];

  const partenza = bot.initialValue;
  const primaData = chiusi[0]?.buyDate ?? bot.startDate ?? bot.createdAt;
  if (partenza != null && Number.isFinite(partenza)) {
    punti.push({ timestamp: new Date(primaData).getTime(), value: partenza });
  }

  for (const t of chiusi) {
    punti.push({ timestamp: new Date(t.sellDate!).getTime(), value: t.exitBalance as number });
  }

  return punti.filter((p) => Number.isFinite(p.timestamp) && Number.isFinite(p.value));
}

/** Massima discesa da un massimo precedente, in percentuale. Sempre >= 0. */
export function drawdownMassimo(punti: PuntoEquityBot[]): number | null {
  if (punti.length < 2) return null;
  let picco = punti[0].value;
  let peggiore = 0;
  for (const p of punti) {
    if (p.value > picco) picco = p.value;
    if (picco > 0) {
      const caduta = ((picco - p.value) / picco) * 100;
      if (caduta > peggiore) peggiore = caduta;
    }
  }
  return peggiore;
}

export interface TernaBot {
  profitto: number | null;
  drawdown: number | null;
  operazioni: number | null;
  /** Perché il drawdown è `n/d`, quando lo è. Mai un vuoto senza causa. */
  motivoDrawdownMancante: string | null;
}

export function ternaBot(bot: Tradebot): TernaBot {
  const operazioni = bot.trades ? bot.trades.length : null;

  const profitto = Number.isFinite(bot.strategyChange ?? NaN)
    ? (bot.strategyChange as number)
    : bot.initialValue != null && bot.finalTotalBalance != null && bot.initialValue !== 0
      ? (bot.finalTotalBalance / bot.initialValue - 1) * 100
      : null;

  const punti = equityDaTrade(bot);
  const drawdown = drawdownMassimo(punti);

  return {
    profitto,
    drawdown,
    operazioni,
    motivoDrawdownMancante:
      drawdown != null
        ? null
        : bot.trades == null
          ? 'le operazioni non sono state chieste al server'
          : punti.length < 2
            ? 'servono almeno due operazioni chiuse'
            : 'i saldi di uscita non sono stati registrati',
  };
}

/* ==========================================================================
 * Periodo di misura — regola 2: nessun numero senza il suo periodo
 * ======================================================================== */

const FORMATO_GIORNO = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

export interface PeriodoBot {
  /** "dal 12 mar 2026 a oggi". */
  etichetta: string;
  /** "5m", "1a 2m". */
  durata: string;
  dal: Date | null;
  al: Date | null;
  /**
   * Da dove viene l'INIZIO, e quindi cosa significa il numero che gli sta
   * accanto. `misura` è l'unico caso in cui l'etichetta e il rendimento parlano
   * dello stesso intervallo; negli altri il periodo comincia prima della misura
   * di tutto il warm-up, e chi legge deve poterlo sapere.
   */
  fonteInizio: 'misura' | 'avvio' | 'creazione' | 'ignota';
}

/**
 * Il periodo su cui i numeri del bot sono misurati.
 *
 * «a oggi» si scrive solo per un bot IN MARCIA, dove è vero: il periodo è
 * aperto e arriva davvero a adesso. Per un bot fermo la fine è l'ultima
 * scrittura del processo, e se non c'è nemmeno quella la fine **non si
 * inventa** — un periodo chiuso con la data di oggi è peggio di un periodo
 * assente, perché non si vede che è sbagliato, e la regola 2 dice che ogni
 * numero porta il suo periodo di misura, non un periodo verosimile.
 */
export function periodoBot(bot: Tradebot): PeriodoBot {
  /* `measuredSince` per primo: è la data da cui il rendimento è DAVVERO
     misurato. Non coincide con la creazione né con l'avvio, perché in mezzo c'è
     il warm-up e a ogni riavvio ce n'è un altro. Scrivere «dal 30 ago» accanto a
     un numero che parte dal 5 set è mettere il numero sotto l'etichetta
     sbagliata — ed è quello che questa schermata faceva. */
  const misura = dataValida(bot.measuredSince);
  const avvio = dataValida(bot.startDate);
  const creazione = dataValida(bot.createdAt);
  const dal = misura ?? avvio ?? creazione;
  const fonteInizio: PeriodoBot['fonteInizio'] = misura
    ? 'misura'
    : avvio
      ? 'avvio'
      : creazione
        ? 'creazione'
        : 'ignota';
  const fineScritta = dataValida(bot.endDate);
  const ultimaScrittura = dataValida(bot.updatedAt);
  const al = fineScritta ?? (bot.isRunning ? new Date() : ultimaScrittura);

  if (!dal) return { etichetta: 'periodo sconosciuto', durata: '', dal: null, al, fonteInizio };

  const inizio = FORMATO_GIORNO.format(dal);

  if (fineScritta) {
    return {
      etichetta: `dal ${inizio} al ${FORMATO_GIORNO.format(fineScritta)}`,
      durata: durataPeriodo(dal, fineScritta),
      dal,
      al,
      fonteInizio,
    };
  }

  if (bot.isRunning) {
    return {
      etichetta: `dal ${inizio} a oggi`,
      durata: durataPeriodo(dal, new Date()),
      dal,
      al,
      fonteInizio,
    };
  }

  if (ultimaScrittura) {
    return {
      etichetta: `dal ${inizio} all’ultima scrittura del ${FORMATO_GIORNO.format(ultimaScrittura)}`,
      durata: durataPeriodo(dal, ultimaScrittura),
      dal,
      al,
      fonteInizio,
    };
  }

  /* Bot fermo, senza data di fine e senza ultima scrittura: la fine non è
     registrata da nessuna parte. Si dice, e la durata resta vuota invece di
     essere calcolata contro adesso. */
  return {
    etichetta: `dal ${inizio}, fine non registrata`,
    durata: '',
    dal,
    al: null,
    fonteInizio,
  };
}

function dataValida(v?: string | null): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ==========================================================================
 * Capitale impegnato
 * ======================================================================== */

/**
 * Quanto capitale ha in gioco questo bot.
 *
 * Non esiste un campo "capitale allocato": il backend ha `currencyBalance` (la
 * valuta di quotazione in mano), `assetBalance` (l'asset in mano) e
 * `parkedCurrency` (la parte messa da parte). Il totale che il bot governa è
 * `finalTotalBalance` quando l'ha scritto, altrimenti la somma di quel che si
 * riesce a leggere. Questa ambiguità è annotata in RICHIESTE-API.md.
 *
 * PERCHÉ NON RESTITUISCE UN NUMERO SOLO.
 * Le tre componenti sono tutte nullabili e prima venivano sommate con `?? 0`:
 * una componente non misurata spariva dentro il totale, che veniva mostrato
 * come completo. Su denaro reale questo è il modo peggiore di sbagliare —
 * la cifra risulta PIÙ BASSA del vero, e chi decide quanto impegnare non ha
 * alcun segno che manchi qualcosa. Un totale che tace una fonte è peggio di
 * nessun totale: qui si dichiara parziale e si nomina cosa manca, come fa la
 * dashboard col patrimonio complessivo.
 */
export interface CapitaleBot {
  /** La cifra, `null` quando non c'è nemmeno una componente misurata. */
  valore: number | null;
  /** `true` quando la cifra somma solo una parte delle componenti. */
  parziale: boolean;
  /** Le componenti non misurate, con il loro nome leggibile. */
  mancanti: string[];
  /** Da dove viene la cifra: cambia cosa significa, e va detto. */
  fonte: 'scritto-dal-bot' | 'somma-delle-parti' | 'valore-iniziale' | 'nessuna';
}

/**
 * SUPERATA da `capitaleDi` in `lib/vitalita.ts`, che non ha più chiamanti qui.
 *
 * Questa versione non conosce `lastSeenPrice` e per valutare l'asset dipende da
 * `posizione()`, che usa `finalAssetPrice`: su un bot in posizione senza
 * operazioni chiuse il prezzo non c'è, e il risultato era «0,00 USDT · parziale»
 * per un bot che teneva migliaia di dollari di asset. Resta esportata finché
 * qualcuno la importa; per una cifra nuova si usa `capitaleDi`.
 */
export function capitaleBot(bot: Tradebot): CapitaleBot {
  /* Quando il bot ha scritto il totale, è il totale: nessuna somma da fare e
     niente da dichiarare parziale. */
  if (Number.isFinite(bot.finalTotalBalance ?? NaN)) {
    return {
      valore: bot.finalTotalBalance as number,
      parziale: false,
      mancanti: [],
      fonte: 'scritto-dal-bot',
    };
  }

  const pos = posizione(bot);

  /* L'asset conta in valuta, quindi serve anche il prezzo. `assetBalance` a
     zero è una misura vera — il bot è fuori mercato — e va distinto da
     `assetBalance` assente, che è una lettura mancante. */
  const asset: number | null =
    bot.assetBalance == null ? null : bot.assetBalance === 0 ? 0 : pos.valore;

  const componenti: { nome: string; valore: number | null }[] = [
    { nome: `valuta in mano (${bot.currency})`, valore: bot.currencyBalance },
    {
      nome:
        bot.assetBalance != null && bot.assetBalance !== 0 && pos.valore == null
          ? `valore dell’asset (${bot.asset}): manca il prezzo`
          : `asset in mano (${bot.asset})`,
      valore: asset,
    },
    { nome: 'valuta parcheggiata', valore: bot.parkedCurrency },
  ];

  const misurate = componenti.filter((c) => c.valore != null);
  const mancanti = componenti.filter((c) => c.valore == null).map((c) => c.nome);

  if (misurate.length === 0) {
    /* Niente di misurato: il valore iniziale è l'unico numero onesto rimasto,
       ma NON è il capitale di adesso e la fonte lo dice. */
    return {
      valore: bot.initialValue ?? null,
      parziale: true,
      mancanti,
      fonte: bot.initialValue != null ? 'valore-iniziale' : 'nessuna',
    };
  }

  return {
    valore: misurate.reduce((somma, c) => somma + (c.valore as number), 0),
    parziale: mancanti.length > 0,
    mancanti,
    fonte: 'somma-delle-parti',
  };
}

/* ==========================================================================
 * Coppia e identità
 * ======================================================================== */

export function coppiaBot(bot: Tradebot): string {
  return `${bot.asset}/${bot.currency}`;
}

/** "Binance · Main" oppure "Wallet 12 · 0x7a…3f". Mai un id nudo. */
export function sedeBot(bot: Tradebot): string {
  if (bot.wallet) {
    const indirizzo = bot.wallet.address;
    /* Molti wallet hanno gia' l'indirizzo nel nome ("Alice 0x8a3...ec756", "0x...0F8c07"):
       aggiungerlo di nuovo lo faceva leggere due volte. */
    const giaNelNome = /0x/i.test(bot.wallet.name ?? '');
    return `${bot.wallet.name}${indirizzo && !giaNelNome ? ` · ${abbrevia(indirizzo)}` : ''}`;
  }
  if (bot.walletId != null) return `wallet #${bot.walletId}`;
  if (bot.account) {
    const exchange = bot.account.exchange?.name;
    return exchange ? `${exchange} · ${bot.account.name}` : bot.account.name;
  }
  if (bot.accountId != null) return `account #${bot.accountId}`;
  return 'nessuna sede';
}

export function abbrevia(indirizzo: string): string {
  return indirizzo.length > 12 ? `${indirizzo.slice(0, 6)}…${indirizzo.slice(-4)}` : indirizzo;
}

/* ==========================================================================
 * Parametri modificabili dal vivo
 * ======================================================================== */

export type ValoreParametro = number | boolean | string;

export interface ParametroModificabile {
  /** Percorso: `strategyParams.stopLoss` oppure `swapSlippage`. */
  chiave: string;
  etichetta: string;
  tipo: 'numero' | 'booleano' | 'testo';
  valore: ValoreParametro;
  /** Unità da mostrare accanto al campo, quando è deducibile dal nome. */
  unita?: string;
  /** Sta dentro `strategyParams` invece che sulla riga del bot. */
  dentroStrategia: boolean;
}

/** Campi della riga del bot che ha senso cambiare mentre opera. */
const CAMPI_VIVI: { chiave: keyof Tradebot; etichetta: string; unita?: string }[] = [
  { chiave: 'currencyBalance', etichetta: 'Capitale in valuta' },
  { chiave: 'parkedCurrency', etichetta: 'Valuta messa da parte' },
  { chiave: 'swapSlippage', etichetta: 'Slippage massimo sullo swap', unita: '%' },
  { chiave: 'gasLimit', etichetta: 'Limite di gas' },
  { chiave: 'minimumBaseCoinBalance', etichetta: 'Saldo minimo di gas' },
  { chiave: 'rechargeQtyBaseCoinBalance', etichetta: 'Ricarica di gas' },
];

/**
 * I parametri che si possono cambiare a bot acceso.
 *
 * `strategyParams` è un oggetto libero: i nomi cambiano da strategia a
 * strategia e non c'è uno schema. Si enumera quello che c'è, invece di
 * indovinare un modulo fisso che mostrerebbe campi inesistenti su metà delle
 * 65 strategie. Un valore che non è scalare non si modifica da qui: si mostra,
 * perché nasconderlo lo farebbe cercare altrove.
 */
export function parametriModificabili(bot: Tradebot): ParametroModificabile[] {
  const fuori: ParametroModificabile[] = CAMPI_VIVI.filter(
    (c) => bot[c.chiave] != null,
  ).map((c) => ({
    chiave: String(c.chiave),
    etichetta: c.etichetta,
    tipo: 'numero',
    valore: Number(bot[c.chiave]),
    unita: c.unita,
    dentroStrategia: false,
  }));

  const dentro: ParametroModificabile[] = Object.entries(bot.strategyParams ?? {})
    .filter(([, v]) => typeof v === 'number' || typeof v === 'boolean' || typeof v === 'string')
    .map(([k, v]) => ({
      chiave: `strategyParams.${k}`,
      etichetta: etichettaParametro(k),
      tipo: typeof v === 'number' ? 'numero' : typeof v === 'boolean' ? 'booleano' : 'testo',
      valore: v as ValoreParametro,
      unita: unitaParametro(k),
      dentroStrategia: true,
    }));

  return [...dentro, ...fuori];
}

/** Parametri di `strategyParams` che non sono scalari: mostrati, non modificabili. */
export function parametriNonModificabili(bot: Tradebot): { chiave: string; valore: string }[] {
  return Object.entries(bot.strategyParams ?? {})
    .filter(([, v]) => v != null && typeof v === 'object')
    .map(([k, v]) => ({ chiave: etichettaParametro(k), valore: JSON.stringify(v) }));
}

/** Da `stopLossPct` a «Stop loss pct». Esportata perche' la usano sia la
 *  modifica dal vivo sia la creazione: due traduzioni diverse dello stesso
 *  nome farebbero sembrare due parametri quello che e' uno solo. */
export function etichettaParametro(chiave: string): string {
  const spaziato = chiave
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();
  return spaziato.charAt(0).toUpperCase() + spaziato.slice(1);
}

function unitaParametro(chiave: string): string | undefined {
  const n = normalizza(chiave);
  if (n.includes('percent') || n.includes('pct') || n.endsWith('perc')) return '%';
  return SOGLIE_PERCENTUALI.includes(classificaSoglia(chiave) ?? '') ? '%' : undefined;
}

/* ==========================================================================
 * Il controllo che il vecchio flusso non aveva:
 * una soglia già superata dalla posizione aperta
 * ======================================================================== */

export type TipoSoglia = 'stop-loss' | 'take-profit';

const SOGLIE_PERCENTUALI: string[] = ['stop-loss', 'take-profit'];

function normalizza(chiave: string): string {
  return chiave.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Riconosce se un parametro è una soglia di uscita.
 *
 * Il riconoscimento è per nome perché non c'è altro: `strategyParams` non ha
 * schema. Un falso negativo costa un avviso mancato — quindi l'elenco è largo,
 * e chi non rientra semplicemente non viene sorvegliato, mai frainteso.
 */
export function classificaSoglia(chiave: string): TipoSoglia | null {
  const n = normalizza(chiave.replace(/^strategyParams\./, ''));

  const perdita = ['stoploss', 'sl', 'stoplosspct', 'stoplosspercent', 'maxloss', 'trailingstop'];
  const guadagno = ['takeprofit', 'tp', 'takeprofitpct', 'takeprofitpercent', 'target', 'profittarget'];

  if (perdita.includes(n)) return 'stop-loss';
  if (guadagno.includes(n)) return 'take-profit';
  return null;
}

export interface AllarmeSoglia {
  tipo: TipoSoglia;
  etichetta: string;
  /** Il valore che si sta per scrivere. */
  nuovo: number;
  /** Dove sta la posizione adesso, in percentuale sull'ingresso. */
  posizionePct: number;
  testo: string;
}

/**
 * Una soglia già superata dalla posizione aperta.
 *
 * Take profit sotto il guadagno attuale, o stop loss sopra la perdita attuale:
 * in entrambi i casi la modifica non protegge la posizione, la CHIUDE alla
 * prossima candela. È il controllo che il vecchio flusso non aveva ed è il
 * motivo per cui il pulsante diventa "Applica comunque": la modifica resta
 * possibile, ma non può passare per una svista.
 */
export function allarmeSoglia(
  chiave: string,
  nuovo: number,
  pos: PosizioneBot,
): AllarmeSoglia | null {
  const tipo = classificaSoglia(chiave);
  if (!tipo || !pos.aperta || pos.variazionePct == null) return null;

  const mossa = pos.variazionePct;
  const segno = mossa >= 0 ? '+' : '−';
  const mossaTesto = `${segno}${num(Math.abs(mossa), 2)}%`;

  if (tipo === 'take-profit' && nuovo > 0 && mossa >= nuovo) {
    return {
      tipo,
      etichetta: 'Take profit già superato',
      nuovo,
      posizionePct: mossa,
      testo: `Il take profit al ${num(nuovo, 1)}% è già superato: la posizione è a ${mossaTesto} e chiuderebbe alla prossima candela invece di restare aperta.`,
    };
  }

  if (tipo === 'stop-loss' && nuovo > 0 && mossa <= -nuovo) {
    return {
      tipo,
      etichetta: 'Stop loss già superato',
      nuovo,
      posizionePct: mossa,
      testo: `Lo stop loss al ${num(nuovo, 1)}% è già superato: la posizione è a ${mossaTesto} e verrebbe chiusa in perdita alla prossima candela invece di essere protetta.`,
    };
  }

  return null;
}

/* ==========================================================================
 * Corpo di creazione di un clone
 * ======================================================================== */

/** Campi che appartengono all'ESECUZIONE del bot sorgente e non alla sua ricetta. */
const NON_SI_CLONA = new Set<keyof Tradebot>([
  'id',
  'createdAt',
  'updatedAt',
  'PID',
  'isRunning',
  'accountId',
  'walletId',
  'initialAssetPrice',
  'initialValue',
  'initialAsset',
  'initialCurrency',
  'assetBalance',
  'currencyBalance',
  'parkedCurrency',
  'marketChange',
  'strategyChange',
  'finalAssetPrice',
  'finalTotalBalance',
  'failedTransactions',
  'errors',
  'subscribers',
  'banned',
  'trades',
  'wallet',
  'account',
  'marketWatcher',
]);

export interface DestinazioneClone {
  tipo: 'account' | 'wallet';
  id: number;
}

/**
 * Il corpo di `POST /tradebots` per un clone.
 *
 * Si copia la RICETTA (strategia, parametri, coppia, timeframe, warm-up,
 * impostazioni di catena) e si azzera la STORIA: saldi, prezzi, errori,
 * operazioni. Copiare `strategyChange` o `finalTotalBalance` farebbe nascere un
 * bot nuovo che dichiara i risultati di un altro.
 *
 * `ValidationPipe` gira senza `whitelist`, quindi i campi in più non vengono
 * scartati dal server: vanno tolti qui.
 */
export function corpoClone(
  sorgente: Tradebot,
  destinazione: DestinazioneClone,
  capitale: number,
  avviaSubito: boolean,
): Partial<Tradebot> {
  const corpo: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(sorgente)) {
    if (NON_SI_CLONA.has(k as keyof Tradebot)) continue;
    if (v === undefined) continue;
    corpo[k] = v;
  }

  if (destinazione.tipo === 'account') {
    corpo.accountId = destinazione.id;
    corpo.walletId = null;
  } else {
    corpo.walletId = destinazione.id;
    corpo.accountId = null;
  }

  /* Il capitale dichiarato è un TETTO solo sui conti di exchange, e questa
     differenza va capita perché cambia cosa succede ai soldi.
     
     Su un conto di exchange `usePartialBalance` serve davvero: più bot devono
     poter operare sullo stesso conto e anche sullo stesso asset, quindi il
     capitale di ciascuno va delimitato, e `currencyBalance` è la sua quota.
     
     Su un wallet no, e non è una mancanza: un bot su catena impegna tutto il
     saldo dell'indirizzo, e per limitarne l'esposizione si crea un wallet
     dedicato — su una catena un secondo indirizzo non costa niente, quindi il
     partizionamento è il wallet stesso. Il flag su quel percorso non viene
     nemmeno letto, e accenderlo darebbe un tetto che nessuno fa rispettare.
     
     Per questo si accende solo per gli exchange: dichiarare un tetto dove non
     esiste è peggio che non offrirlo, perché chi clona su un wallet da
     diecimila scrivendo cinquecento crederebbe di rischiare cinquecento. */
  corpo.currencyBalance = capitale;
  corpo.assetBalance = 0;
  corpo.usePartialBalance = destinazione.tipo !== 'wallet';
  corpo.isRunning = avviaSubito;

  /* La data di avvio è del clone, non della sorgente: copiarla farebbe
     dichiarare a un bot nato oggi un periodo di misura che non ha vissuto, e
     tutte le sue percentuali verrebbero lette su quel periodo sbagliato.
     `ValidationPipe` gira senza `transform`, quindi la data va già nella forma
     che il server si aspetta: la stessa ISO che rilegge dalle sue righe. */
  if (sorgente.startDate) corpo.startDate = new Date().toISOString();

  return corpo as Partial<Tradebot>;
}
